import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { setChannels, setCurrentChannel, setTotalUnreadCount } from '../../store/chatSlice';
import { 
  markChannelAsRead, 
  generateToken, 
  getOrCreateChannel,
  getChatToken,
  getDoctorChats,
  getNutritionistChats,
  getAdminChats,
  getTotalUnreadCount
} from '../../api/chat';
import { useChatContext } from 'stream-chat-react';
import ChatList from '../../components/Chat/ChatList';
import ChatWindow from '../../components/Chat/ChatWindow';
import './Chat.scss';
import { useNavigate } from 'react-router-dom';
import { UserRoleEnum } from '../../types/apiGeneratedTypes';

const Chat: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [streamChannels, setStreamChannels] = useState<any[]>([]);
  const [selectedChannel, setSelectedChannel] = useState<any>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isLoadingChats, setIsLoadingChats] = useState(false);
  const [activeTab, setActiveTab] = useState<'doctors' | 'support'>('doctors');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  const { currentChannelId } = useSelector((state: RootState) => state.chat);
  const admin = useSelector((state: RootState) => state.admin.admin);
  const membersList = useSelector((state: RootState) => state.members.membersList);
  const membersData = useMemo(() => membersList || [], [membersList]);

  useEffect(() => {
    if (admin.role === UserRoleEnum.Nutritionist) {
      navigate('/members');
    }
  }, [admin.role, navigate]);

  const { client } = useChatContext();
  const prevChannelsRef = useRef<string>('');
  const isConnectingRef = useRef(false);
  const channelListenersRef = useRef<Map<string, any>>(new Map());

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 400);

    return () => {
      window.clearTimeout(handle);
    };
  }, [searchQuery]);

  const formattedChannels = useMemo(() => {
    if (streamChannels.length === 0) return [];
    
    return streamChannels.map((ch: any) => {
      const backendData = ch._backendData;
      const memberId = backendData?.memberId || ch.data?.memberId;
      const memberName = backendData?.memberName || ch.data?.memberName || 'Unknown Member';
      const providerId = backendData?.providerId || ch.data?.providerId;
      const providerRole = backendData?.providerRole || ch.data?.providerRole || 'support';

      const member = membersData.find((m: any) => m.unique_id === memberId || m.id === memberId);

      let lastMessage = null;
      const messages = ch.state?.messages || [];
      if (messages.length > 0) {
        const msg = messages[messages.length - 1];
        lastMessage = {
          id: msg.id,
          text: msg.text,
          user: msg.user?.id || msg.user_id,
          created_at: msg.created_at ? new Date(msg.created_at).toISOString() : null,
        };
      }

      let unreadCount = 0;
      if (backendData?.unread_count !== undefined && backendData?.unread_count !== null) {
        unreadCount = Number(backendData.unread_count) || 0;
      } else if (ch.state?.unreadCount !== undefined && ch.state?.unreadCount !== null) {
        unreadCount = Number(ch.state.unreadCount) || 0;
      }

      const isRealStreamChannel = ch && typeof ch.watch === 'function';
      
      return {
        id: ch.id,
        name: providerRole === 'doctor' ? 'Doctor' : providerRole === 'nutritionist' ? 'Nutritionist' : 'Support',
        memberId,
        memberName,
        memberUniqueId: member?.unique_id || member?.id || memberId,
        providerId,
        providerName: ch.data?.providerName || '',
        providerRole,
        unreadCount,
        lastMessage,
        _streamChannel: isRealStreamChannel ? ch : undefined,
      };
    });
  }, [streamChannels, membersData]);

  const filteredChannels = useMemo(() => {
    let filtered = [];
    if (activeTab === 'doctors') {
      filtered = formattedChannels.filter(ch => ch.providerRole === 'doctor');
    } else if (activeTab === 'support') {
      filtered = formattedChannels.filter(ch => ch.providerRole === 'support');
    } else {
      filtered = formattedChannels;
    }

    return [...filtered].sort((a, b) => {
      const aUnread = (a.unreadCount || 0) > 0;
      const bUnread = (b.unreadCount || 0) > 0;

   
      if (aUnread && !bUnread) return -1;
      if (!aUnread && bUnread) return 1;

      
      if (aUnread && bUnread) {
        const aTime = a.lastMessage?.created_at ? new Date(a.lastMessage.created_at).getTime() : 0;
        const bTime = b.lastMessage?.created_at ? new Date(b.lastMessage.created_at).getTime() : 0;
        return bTime - aTime;
      }

    
      return 0;
    });
  }, [formattedChannels, activeTab]);

  useEffect(() => {
    if (formattedChannels.length > 0) {
      const serializableChannels = formattedChannels.map(({ _streamChannel, ...rest }) => rest);
      
      const channelsKey = serializableChannels.map(ch => `${ch.id}-${ch.unreadCount}`).join(',');
      
      if (channelsKey !== prevChannelsRef.current) {
        prevChannelsRef.current = channelsKey;
        dispatch(setChannels(serializableChannels));
      }
    }
  }, [formattedChannels, dispatch]);

  useEffect(() => {
    let isMounted = true;
    
    const fetchChatsByType = async () => {
      if (!admin.is_super_admin && admin.role !== UserRoleEnum.Admin && admin.role !== UserRoleEnum.SuperAdmin) {
        setIsLoadingChats(false);
        return;
      }

      try {
        setIsLoadingChats(true);
        const chatType = activeTab === 'doctors' ? 'doctors' : 'admins';
        const nameParam = debouncedSearchQuery.trim();
        const backendResponse = await getAdminChats(chatType, nameParam.length > 0 ? nameParam : undefined);
        
        if (!client || !client.userID) {
          setIsLoadingChats(false);
          return;
        }
        
        if (backendResponse?.chats && backendResponse.chats.length > 0) {
          const channelIds = backendResponse.chats
            .map((chat: any) => chat.external_channel_id)
            .filter((id: any) => id);
          
          const BATCH_SIZE = 100;
          let allStreamChats: any[] = [];
          
          for (let i = 0; i < channelIds.length; i += BATCH_SIZE) {
            const batchIds = channelIds.slice(i, i + BATCH_SIZE);
            try {
              const batchChats = await client.queryChannels(
                { 
                  type: 'messaging',
                  id: { $in: batchIds }
                },
                { last_message_at: -1 },
                { watch: true, state: true }
              );
              allStreamChats = [...allStreamChats, ...batchChats];
            } catch (error) {
              console.error(`Error querying batch ${i / BATCH_SIZE + 1}:`, error);
            }
          }
          
          const streamChats = allStreamChats;
          
          streamChats.forEach((channel: any) => {
            const backendChat = backendResponse.chats.find(
              (chat: any) => chat.external_channel_id === channel.id
            );
            
            if (backendChat) {
              (channel as any)._backendData = {
                memberId: backendChat.member_id,
                memberName: backendChat.member_name,
                providerId: backendChat.assigned_specialist_id,
                providerRole: chatType === 'doctors' ? 'doctor' : 'support',
                chat_type: backendChat.chat_type,
                unread_count: backendChat.unread_count,
              };
            }
          });
          
          const foundIds = new Set(streamChats.map((ch: any) => ch.id));
          const placeholderChannels = backendResponse.chats
            .filter((chat: any) => !foundIds.has(chat.external_channel_id))
            .map((chat: any) => {
              const placeholder: any = {
                id: chat.external_channel_id,
                data: {
                  memberId: chat.member_id,
                  memberName: chat.member_name,
                  providerId: chat.assigned_specialist_id,
                  providerRole: chatType === 'doctors' ? 'doctor' : 'support',
                  providerName: chat.assigned_specialist_name || '',
                },
                state: { messages: [] },
              };
              (placeholder as any)._backendData = {
                memberId: chat.member_id,
                memberName: chat.member_name,
                providerId: chat.assigned_specialist_id,
                providerRole: chatType === 'doctors' ? 'doctor' : 'support',
                chat_type: chat.chat_type,
                unread_count: chat.unread_count,
              };
              return placeholder;
            });

          const allChannels = [...streamChats, ...placeholderChannels];
          setStreamChannels(allChannels);
          
          streamChats.forEach((channel: any) => {
            const existingListener = channelListenersRef.current.get(channel.id);
            if (existingListener) {
              channel.off('message.new', existingListener.messageNew);
              channel.off('message.read', existingListener.messageRead);
            }
            
            const handleMessageNew = (event: any) => {
              if (isMounted && event.user?.id !== admin.user_id && event.message) {
                setStreamChannels((prevChannels) => {
                  return prevChannels.map((ch) => {
                    if (ch.id === channel.id) {
                      const updatedChannel = { ...ch };
                      if (updatedChannel._backendData) {
                        updatedChannel._backendData = {
                          ...updatedChannel._backendData,
                          unread_count: (updatedChannel._backendData.unread_count || 0) + 1
                        };
                      }
                      return updatedChannel;
                    }
                    return ch;
                  });
                });
              }
            };
            
            const handleMessageRead = () => {
              if (isMounted) {
                setStreamChannels((prevChannels) => {
                  return prevChannels.map((ch) => {
                    if (ch.id === channel.id) {
                      const updatedChannel = { ...ch };
                      if (updatedChannel._backendData) {
                        updatedChannel._backendData = {
                          ...updatedChannel._backendData,
                          unread_count: 0
                        };
                      }
                      return updatedChannel;
                    }
                    return ch;
                  });
                });
              }
            };
            
            channel.on('message.new', handleMessageNew);
            channel.on('message.read', handleMessageRead);
            
            channelListenersRef.current.set(channel.id, {
              messageNew: handleMessageNew,
              messageRead: handleMessageRead
            });
          });
          
        } else {
          setStreamChannels([]);
        }
      } catch (error) {
        console.error('Error fetching chats by type:', error);
        setStreamChannels([]);
      } finally {
        setIsLoadingChats(false);
      }
    };

    if (admin.user_id && (admin.is_super_admin || admin.role === UserRoleEnum.Admin || admin.role === UserRoleEnum.SuperAdmin)) {
      fetchChatsByType();
    }
    
    return () => {
      isMounted = false;
    };
  }, [activeTab, debouncedSearchQuery, admin.user_id, admin.role, admin.is_super_admin, client, client?.userID]);

  useEffect(() => {
    const fetchTotalUnreadCount = async () => {
      if (!admin.user_id) {
        return;
      }

      try {
        const totalCount = await getTotalUnreadCount();
        dispatch(setTotalUnreadCount(totalCount));
      } catch (error) {
        console.error('Error fetching total unread count:', error);
      }
    };

    fetchTotalUnreadCount();
    const interval = setInterval(fetchTotalUnreadCount, 30000);
    
    return () => clearInterval(interval);
  }, [admin.user_id, dispatch]);

  const getFiltersForRole = useCallback((role: string, isSuperAdmin: boolean) => {
    if (isSuperAdmin || role === UserRoleEnum.Admin || role === UserRoleEnum.SuperAdmin) {
      return {
        type: 'messaging',
        members: { $in: [admin.user_id] }
      };
    }

    if (role === 'doctor') {
      return { 
        type: 'messaging', 
        providerRole: 'doctor',
        members: { $in: [admin.user_id] }
      };
    }

    if (role === 'nutritionist') {
      return { 
        type: 'messaging', 
        providerRole: 'nutritionist',
        members: { $in: [admin.user_id] }
      };
    }

    return { type: 'messaging' };
  }, [admin.user_id]);

  useEffect(() => {
    let isMounted = true;
    const channelListeners = channelListenersRef.current;
    
    const initializeChat = async () => {
      try {
        setIsConnecting(true);
        
        let token: string;
        let userId: string = admin.user_id;
        
        try {
          const backendAuth = await getChatToken();
          token = backendAuth.token;
          userId = backendAuth.user_id;
        } catch (error: any) {
          token = await generateToken(admin.user_id);
          userId = admin.user_id;
        }
        
        if (!client) {
          console.warn('Stream Chat client not initialized yet');
          return;
        }
        
        if (isConnectingRef.current) {
          return;
        }
        
        if (client.userID !== admin.user_id) {
          isConnectingRef.current = true;
          
          if (client.userID && client.userID !== userId) {
            await client.disconnectUser();
          }
          
          await client.connectUser(
            {
              id: userId,
              name: `${admin.first_name} ${admin.last_name}`.trim() || admin.email,
              role: admin.role,
              image: `https://ui-avatars.com/api/?name=${encodeURIComponent((`${admin.first_name} ${admin.last_name}`.trim() || admin.email) as string)}&background=238698&color=ffffff&rounded=true&size=128`,
            },
            token
          );
          
          isConnectingRef.current = false;
        }

        const isAdmin = admin.is_super_admin || admin.role === UserRoleEnum.Admin || admin.role === UserRoleEnum.SuperAdmin;

        if (!isAdmin && client.userID) {
          setIsLoadingChats(true);
          
          try {
            let backendResponse: any;
            if (admin.role === 'doctor') {
              backendResponse = await getDoctorChats();
            } else if (admin.role === 'nutritionist') {
              backendResponse = await getNutritionistChats();
            } else {
              setIsLoadingChats(false);
              return;
            }
            
            if (backendResponse?.chats && backendResponse.chats.length > 0) {
              const channelIds = backendResponse.chats
                .map((chat: any) => chat.external_channel_id)
                .filter((id: any) => id);
              
              let streamChats: any[] = [];
              
              if (channelIds.length > 0) {
                try {
                  const BATCH_SIZE = 100;
                  let allStreamChats: any[] = [];
                  
                  for (let i = 0; i < channelIds.length; i += BATCH_SIZE) {
                    const batchIds = channelIds.slice(i, i + BATCH_SIZE);
                    try {
                      const batchChats = await client.queryChannels(
                        { 
                          type: 'messaging',
                          id: { $in: batchIds }
                        },
                        { last_message_at: -1 },
                        { 
                          watch: true, 
                          state: true
                        }
                      );
                      allStreamChats = [...allStreamChats, ...batchChats];
                    } catch (error) {
                      console.error(`Error querying batch ${i / BATCH_SIZE + 1}:`, error);
                    }
                  }
                  
                  streamChats = allStreamChats;
                } catch (error) {
                  console.error('Error querying Stream channels:', error);
                  streamChats = [];
                }
              }
              
              streamChats.forEach((channel: any) => {
                const backendChat = backendResponse.chats.find(
                  (chat: any) => chat.external_channel_id === channel.id
                );
                
                if (backendChat) {
                  (channel as any)._backendData = {
                    memberId: backendChat.member_id,
                    memberName: backendChat.member_name,
                    providerId: backendChat.assigned_specialist_id,
                    providerRole: admin.role,
                    chat_type: backendChat.chat_type,
                    unread_count: backendChat.unread_count,
                  };
                }
              });
              
              const foundIds = new Set(streamChats.map((ch: any) => ch.id));
              const placeholderChannels = backendResponse.chats
                .filter((chat: any) => {
                  const channelId = chat.external_channel_id;
                  return !channelId || !foundIds.has(channelId);
                })
                .map((chat: any) => {
                  const channelId = chat.external_channel_id || `temp-${chat.member_id}-${chat.assigned_specialist_id}`;
                  const placeholder: any = {
                    id: channelId,
                    data: {
                      memberId: chat.member_id,
                      memberName: chat.member_name,
                      providerId: chat.assigned_specialist_id,
                      providerRole: admin.role,
                      providerName: chat.assigned_specialist_name || '',
                    },
                    state: { messages: [] },
                  };
                  (placeholder as any)._backendData = {
                    memberId: chat.member_id,
                    memberName: chat.member_name,
                    providerId: chat.assigned_specialist_id,
                    providerRole: admin.role,
                    chat_type: chat.chat_type,
                    unread_count: chat.unread_count || 0,
                  };
                  return placeholder;
                });
              
              const allChannels = [...streamChats, ...placeholderChannels];
              
              if (isMounted) {
                setStreamChannels(allChannels);
                setIsLoadingChats(false);
                
                streamChats.forEach((channel: any) => {
                  const existingListener = channelListeners.get(channel.id);
                  if (existingListener) {
                    channel.off('message.new', existingListener.messageNew);
                    channel.off('message.read', existingListener.messageRead);
                  }
                  
                  const handleMessageNew = (event: any) => {
                    if (isMounted && event.user?.id !== admin.user_id && event.message) {
                      setStreamChannels((prevChannels) => {
                        return prevChannels.map((ch) => {
                          if (ch.id === channel.id) {
                            const updatedChannel = { ...ch };
                            if (updatedChannel._backendData) {
                              updatedChannel._backendData = {
                                ...updatedChannel._backendData,
                                unread_count: (updatedChannel._backendData.unread_count || 0) + 1
                              };
                            }
                            return updatedChannel;
                          }
                          return ch;
                        });
                      });
                    }
                  };
                  
                  const handleMessageRead = () => {
                    if (isMounted) {
                      setStreamChannels((prevChannels) => {
                        return prevChannels.map((ch) => {
                          if (ch.id === channel.id) {
                            const updatedChannel = { ...ch };
                            if (updatedChannel._backendData) {
                              updatedChannel._backendData = {
                                ...updatedChannel._backendData,
                                unread_count: 0
                              };
                            }
                            return updatedChannel;
                          }
                          return ch;
                        });
                      });
                    }
                  };
                  
                  channel.on('message.new', handleMessageNew);
                  channel.on('message.read', handleMessageRead);
                  
                  channelListeners.set(channel.id, {
                    messageNew: handleMessageNew,
                    messageRead: handleMessageRead,
                    channel,
                  });
                });
              }
            } else {
              const filters = getFiltersForRole(admin.role, admin.is_super_admin);
              const queriedChannels = await client.queryChannels(filters, { last_message_at: -1 }, {
                watch: false,
                state: false,
              });
              
              if (isMounted) {
                setStreamChannels(queriedChannels);
                setIsLoadingChats(false);
              }
            }
          } catch (error) {
            console.error('Error fetching role chats:', error);
            try {
              const filters = getFiltersForRole(admin.role, admin.is_super_admin);
              const queriedChannels = await client.queryChannels(filters, { last_message_at: -1 }, {
                watch: false,
                state: false,
              });
              
              if (isMounted) {
                setStreamChannels(queriedChannels);
                setIsLoadingChats(false);
              }
            } catch (fallbackError) {
              console.error('Error in fallback query:', fallbackError);
              if (isMounted) {
                setIsLoadingChats(false);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error initializing chat:', error);
        if (isMounted) {
          setIsLoadingChats(false);
        }
      } finally {
        if (isMounted) {
          setIsConnecting(false);
        }
      }
    };

    if (admin.user_id) {
      initializeChat();
    }

    return () => {
      isMounted = false;
      channelListeners.forEach((listeners) => {
        if (listeners.channel?.off) {
          listeners.channel.off('message.new', listeners.messageNew);
          listeners.channel.off('message.read', listeners.messageRead);
        }
      });
      channelListeners.clear();
    };
  }, [admin.user_id, admin.role, admin.is_super_admin, admin.email, admin.first_name, admin.last_name, client, getFiltersForRole]);

  const handleSelectChannel = async (formattedChannel: any) => {
    let channelToSelect = formattedChannel;
    
    if (!formattedChannel._streamChannel) {
      let providerId = formattedChannel.providerId;
      let memberId = formattedChannel.memberId;
      let providerRole = formattedChannel.providerRole || 'support';
      
      if ((!providerId || !memberId) && formattedChannel.id) {
        const originalChannel = streamChannels.find((ch: any) => ch.id === formattedChannel.id);
        if (originalChannel) {
          const backendData = originalChannel._backendData;
          providerId = providerId || backendData?.providerId || originalChannel.data?.providerId;
          memberId = memberId || backendData?.memberId || originalChannel.data?.memberId;
          providerRole = providerRole || backendData?.providerRole || originalChannel.data?.providerRole || 'support';
          
          if ((!providerId || !memberId) && originalChannel.state?.members) {
            const members = Object.keys(originalChannel.state.members);
            const otherMember = members.find((id: string) => id !== admin.user_id);
            if (otherMember && !memberId) {
              if (admin.is_super_admin || admin.role === UserRoleEnum.Admin || admin.role === UserRoleEnum.SuperAdmin) {
                memberId = otherMember;
              } else {
                providerId = otherMember;
              }
            }
          }
          
          if (!providerId && formattedChannel.id) {
            const idMatch = formattedChannel.id.match(/^(doctor|nutritionist|support)-(.+)$/);
            if (idMatch) {
              providerRole = idMatch[1] as 'doctor' | 'nutritionist' | 'support';
              providerId = idMatch[2];
            }
          }
        }
      }
      
      // Handle admins_ prefix for support channels
      if (!providerId && formattedChannel.id && formattedChannel.id.startsWith('admins_')) {
        providerRole = 'support';
        const extractedMemberId = formattedChannel.id.replace('admins_', '');
        if (!memberId) {
          memberId = extractedMemberId;
        }
      }
      
      if (!providerId && formattedChannel.id) {
        const idMatch = formattedChannel.id.match(/^(doctor|nutritionist|support)-(.+)$/);
        if (idMatch) {
          providerRole = idMatch[1] as 'doctor' | 'nutritionist' | 'support';
          providerId = idMatch[2];
        }
      }

      if (admin.is_super_admin || admin.role === UserRoleEnum.Admin || admin.role === UserRoleEnum.SuperAdmin) {
        if (!memberId && formattedChannel.memberName) {
          const member = membersData.find((m: any) => 
            `${m.first_name} ${m.last_name}`.trim().toLowerCase() === formattedChannel.memberName?.toLowerCase()
          );
          if (member) {
            memberId = member.unique_id || member.id;
          }
        }
        
        if (providerRole === 'support' && !providerId && formattedChannel.id) {
        }
      } else {
        if (!providerId) {
          providerId = admin.user_id;
        }
      }
      
      if (client && memberId) {
        try {
          let streamChannel: any = null;
          
          if (formattedChannel.id) {
            try {
              const existingChannels = await client.queryChannels(
                { type: 'messaging', id: formattedChannel.id },
                { last_message_at: -1 },
                { watch: true, state: true }
              );
              if (existingChannels.length > 0) {
                streamChannel = existingChannels[0];
              }
            } catch (queryError) {
              console.error('Error querying channel by ID:', queryError);
            }
          }
          
          if (!streamChannel && providerId) {
            const expectedChannelId = `${providerRole}-${providerId}`;
            if (formattedChannel.id && formattedChannel.id !== expectedChannelId) {
              try {
                const existingChannels = await client.queryChannels(
                  { type: 'messaging', id: expectedChannelId },
                  { last_message_at: -1 },
                  { watch: true, state: true }
                );
                if (existingChannels.length > 0) {
                  streamChannel = existingChannels[0];
                }
              } catch (queryError) {
              }
            }
            
            if (!streamChannel) {
              streamChannel = await getOrCreateChannel(
                client,
                memberId,
                providerId,
                providerRole,
                formattedChannel.memberName,
                formattedChannel.providerName
              );
            }
          } else if (!streamChannel && providerRole === 'support' && formattedChannel.id) {
            try {
              streamChannel = client.channel('messaging', formattedChannel.id, {
                members: [memberId, admin.user_id],
                created_by_id: memberId,
                data: {
                  memberId,
                  providerId: admin.user_id,
                  providerRole: 'support',
                  memberName: formattedChannel.memberName,
                  providerName: admin.first_name && admin.last_name ? `${admin.first_name} ${admin.last_name}`.trim() : admin.email,
                },
              } as any);
              await streamChannel.watch();
            } catch (createError) {
              console.error('Error creating support channel:', createError);
              throw createError;
            }
          }
          
          if (streamChannel) {
            channelToSelect = {
              ...formattedChannel,
              _streamChannel: streamChannel,
              providerId: providerId || admin.user_id,
              memberId,
              providerRole
            };
            
            setStreamChannels((prevChannels) => {
              return prevChannels.map((ch) => {
                if (ch.id === formattedChannel.id) {
                  return {
                    ...ch,
                    _streamChannel: streamChannel,
                    _backendData: ch._backendData ? {
                      ...ch._backendData,
                      unread_count: 0
                    } : undefined
                  };
                }
                return ch;
              });
            });
          } else {
            console.error('Cannot create channel: stream channel not available', {
              hasClient: !!client,
              providerId,
              memberId,
              channelId: formattedChannel.id,
              formattedChannel
            });
            return;
          }
        } catch (error) {
          console.error('Error creating channel:', error);
          return;
        }
      } else {
        console.error('Cannot create channel: missing required data', {
          hasClient: !!client,
          providerId,
          memberId,
          channelId: formattedChannel.id,
          formattedChannel
        });
        return;
      }
    }
    
    if (!channelToSelect._streamChannel) {
      console.error('No Stream channel available for selection');
      return;
    }
    
    setSelectedChannel(channelToSelect);
    dispatch(setCurrentChannel(channelToSelect.id));
    
    try {
      await markChannelAsRead(channelToSelect.id, admin.user_id);
      
      setStreamChannels((prevChannels) => {
        return prevChannels.map((ch) => {
          if (ch.id === channelToSelect.id) {
            const updatedChannel = { ...ch };
            if (updatedChannel._backendData) {
              updatedChannel._backendData = {
                ...updatedChannel._backendData,
                unread_count: 0
              };
            }
            return updatedChannel;
          }
          return ch;
        });
      });
      
      try {
        const totalCount = await getTotalUnreadCount();
        dispatch(setTotalUnreadCount(totalCount));
      } catch (error) {
        console.error('Error refreshing total unread count:', error);
      }
    } catch (error) {
      console.error('Error marking channel as read:', error);
    }
  };

  const handleMemberClick = (memberId: string) => {
    navigate(`/members/${memberId}`);
  };

  if (isConnecting) {
    return (
      <div className="chat-loading">
        <p>Loading chat...</p>
      </div>
    );
  }

  if (admin.role === UserRoleEnum.Nutritionist) {
    return null;
  }

  const isAdmin = admin.is_super_admin || admin.role === UserRoleEnum.Admin || admin.role === UserRoleEnum.SuperAdmin;

  return (
    <div className="chat-container">
      <div className="chat-layout">
        <div className="chat-sidebar">
          {isAdmin ? (
            <>
              <div className="chat-tabs">
                <button 
                  className={activeTab === 'doctors' ? 'active' : ''}
                  onClick={() => setActiveTab('doctors')}
                >
                  Doctors
                </button>
                <button 
                  className={activeTab === 'support' ? 'active' : ''}
                  onClick={() => setActiveTab('support')}
                >
                  Support
                </button>
              </div>

              <ChatList
                channels={filteredChannels}
                selectedChannelId={currentChannelId}
                onSelectChannel={handleSelectChannel}
                onMemberClick={handleMemberClick}
                isLoading={isLoadingChats}
                searchQuery={searchQuery}
                onSearchQueryChange={setSearchQuery}
              />
            </>
          ) : (
            <ChatList
              channels={formattedChannels}
              selectedChannelId={currentChannelId}
              onSelectChannel={handleSelectChannel}
              onMemberClick={handleMemberClick}
              isLoading={isLoadingChats}
              searchQuery={searchQuery}
              onSearchQueryChange={setSearchQuery}
            />
          )}
        </div>
        <div className="chat-main">
          {selectedChannel && selectedChannel._streamChannel ? (
            <ChatWindow channel={selectedChannel} />
          ) : selectedChannel ? (
            <div className="chat-empty">
              <p>Loading conversation...</p>
            </div>
          ) : (
            <div className="chat-empty">
              <p>Select a conversation to start chatting</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Chat;

